import React, { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getFuelColor } from "../../../utils/fuelLabelColor";

const TOTAL_KEY = "Total";
const VARIANTS = {
  sales: {
    totalColor: "#0f766e",
    stackId: "fuel-sales",
    gradientPrefix: "emp-sales",
    emptyTitle: "No sales trend data yet.",
    emptyHint: "Daily fuel sales appear here after nozzle closing readings are recorded.",
  },
  purchase: {
    totalColor: "#c2410c",
    stackId: "fuel-purchase",
    gradientPrefix: "emp-purchase",
    emptyTitle: "No purchase trend data yet.",
    emptyHint: "Daily fuel purchases appear here after approved fuel unloading entries are recorded.",
  },
};

const RANGE_OPTIONS = [
  { key: "7", label: "7 days" },
  { key: "14", label: "14 days" },
  { key: "30", label: "30 days" },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatLitres = (value) =>
  `${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} L`;

const formatCompactLitres = (value) => {
  const num = Number(value || 0);
  if (num >= 100000) {
    return `${(num / 100000).toFixed(1)}L`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k L`;
  }
  return `${Math.round(num)} L`;
};

const buildGradientId = (prefix, key) =>
  `${prefix}-${String(key).replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`;

const SalesTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => toNumber(b.value) - toNumber(a.value));

  return (
    <div className="emp-sales-trend__tooltip">
      <p className="emp-sales-trend__tooltip-title">{label}</p>
      {rows.map((item) => (
        <div className="emp-sales-trend__tooltip-row" key={item.dataKey}>
          <span
            className="emp-sales-trend__tooltip-dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="emp-sales-trend__tooltip-label">{item.name}</span>
          <span className="emp-sales-trend__tooltip-value">
            {formatLitres(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="emp-sales-trend__legend">
      {payload.map((entry) => (
        <span className="emp-sales-trend__legend-item" key={entry.value}>
          <span
            className="emp-sales-trend__legend-swatch"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const EmployeeSalesTrendChart = ({
  data = [],
  fuels = [],
  rangeDays = "30",
  onRangeChange,
  variant = "sales",
}) => {
  const config = VARIANTS[variant] || VARIANTS.sales;
  const fuelSeries = useMemo(
    () => (fuels || []).filter((name) => Boolean(name)),
    [fuels],
  );

  const chartData = useMemo(() => {
    const limit = Number(rangeDays) || 30;
    const sliced = (data || []).slice(-limit);

    return sliced.map((point) => {
      const total = fuelSeries.reduce(
        (sum, fuel) => sum + toNumber(point[fuel]),
        0,
      );
      return { ...point, [TOTAL_KEY]: total };
    });
  }, [data, fuelSeries, rangeDays]);

  const summary = useMemo(() => {
    let periodTotal = 0;
    let peakValue = 0;
    let peakLabel = "—";
    let activeDays = 0;

    chartData.forEach((point) => {
      const total = toNumber(point[TOTAL_KEY]);
      if (total <= 0) {
        return;
      }
      periodTotal += total;
      activeDays += 1;
      if (total > peakValue) {
        peakValue = total;
        peakLabel = point.name;
      }
    });

    return {
      periodTotal,
      peakValue,
      peakLabel,
      average: activeDays > 0 ? periodTotal / activeDays : 0,
      activeDays,
    };
  }, [chartData]);

  if (!chartData.length) {
    return (
      <div className="emp-sales-trend__empty">
        <i className="fa fa-line-chart emp-sales-trend__empty-icon" aria-hidden="true" />
        <p>{config.emptyTitle}</p>
        <small>{config.emptyHint}</small>
      </div>
    );
  }

  return (
    <div className="emp-sales-trend">
      <div className="emp-sales-trend__toolbar">
        <div className="emp-sales-trend__kpis">
          <div className="emp-sales-trend__kpi emp-sales-trend__kpi--brand">
            <span>Period total</span>
            <strong>{formatLitres(summary.periodTotal)}</strong>
          </div>
          <div className="emp-sales-trend__kpi emp-sales-trend__kpi--blue">
            <span>Daily average</span>
            <strong>{formatLitres(summary.average)}</strong>
          </div>
          <div className="emp-sales-trend__kpi emp-sales-trend__kpi--green">
            <span>Best day</span>
            <strong>{formatLitres(summary.peakValue)}</strong>
            <small>{summary.peakLabel}</small>
          </div>
          <div className="emp-sales-trend__kpi emp-sales-trend__kpi--amber">
            <span>Active days</span>
            <strong>{summary.activeDays}</strong>
          </div>
        </div>
        <div className="emp-dashboard__filter-pills">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`btn btn-sm ${rangeDays === option.key ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => onRangeChange?.(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="emp-sales-trend__chart">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
          >
            <defs>
              {fuelSeries.map((fuel) => {
                const color = getFuelColor(fuel);
                const id = buildGradientId(config.gradientPrefix, fuel);
                return (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.08} />
                  </linearGradient>
                );
              })}
            </defs>

            <CartesianGrid stroke="#e8edf3" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 11 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={6}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tickFormatter={formatCompactLitres}
              tick={{ fill: "#64748b", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={64}
            />
            <Tooltip content={<SalesTrendTooltip />} />
            <Legend content={<LegendContent />} />

            {fuelSeries.map((fuel) => {
              const color = getFuelColor(fuel);
              return (
                <Area
                  key={fuel}
                  type="monotone"
                  dataKey={fuel}
                  name={fuel}
                  stackId={config.stackId}
                  stroke={color}
                  fill={`url(#${buildGradientId(config.gradientPrefix, fuel)})`}
                  strokeWidth={2}
                  connectNulls
                  activeDot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: color }}
                />
              );
            })}

            <Line
              type="monotone"
              dataKey={TOTAL_KEY}
              name="Total"
              stroke={config.totalColor}
              strokeWidth={2.5}
              dot={{ r: 2.5, fill: config.totalColor, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: config.totalColor, stroke: "#fff", strokeWidth: 2 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EmployeeSalesTrendChart;
